'use client';

import { Button, Sheet, SheetContent, SheetFooter } from '@shared/components/shadcn';
import { getSiteConfig } from '@shared/config';
import { InfoCircle } from '@shared/icons';
import { twMerge } from '@shared/themes';
import { NumberFormats as nf, ROUTES, useCurrencyFormatter, useIsMobile } from '@shared/utils';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import Link from 'next/link';
import { IconWithTooltip } from '../../../filters/components/IconWithTooltip/IconWithTooltip';
import { GraphicContainer } from '../../../GraphicContainer/GraphicContainer';
import { PromotionPodGraphicContainer, PromotionPodHeader } from '../../../PromotionPod/components/PromotionPodComponents';
import { PromotionProgressBar } from '../../../PromotionPod/components/PromotionProgressBar/PromotionProgressBar';
import type { PromoType } from '../../../promotions/types/types';
import type { ContentType } from '../../../types';
import type { Amount } from '../../types';

type BonusInfoModalProps = {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  data: Partial<ContentType>;
  variant?: PromoType;
  className?: string;
};

function BonusInfoModal({ isOpen, onOpenChange, data, className, variant = 'bonuses' }: BonusInfoModalProps) {
  const t = useTranslations('offers.page.obs.offer.bonusInfo');
  const isMobile = useIsMobile();
  const { showMaxLimits } = getSiteConfig().BONUS_INFO;
  const {
    image,
    status,
    gameLogo,
    expirationDate,
    title,
    description,
    bonusBalanceText,
    bonusProgressText,
    amount,
    activationDate,
    bundleCode,
    iterationNr,
    maxBet,
    maxWinnings,
    validOn,
  } = data;
  const currencyFormatter = useCurrencyFormatter();

  const classes = {
    container: twMerge('rounded-md overflow-hidden border border-border flex flex-col', className),
    contentContainer: twMerge('p-4 flex flex-col gap-4'),
  };

  const shouldShowProgressBar = data.progress && bonusProgressText && status !== 'pending';

  const formatCurrency = (amount: Pick<Amount, 'fixed_amount' | 'currency'>) => {
    return currencyFormatter(amount.fixed_amount).format(nf.WITH_DECIMALS).get();
  };

  const formatAmountOrNA = (amount?: Pick<Amount, 'fixed_amount' | 'currency'>) => {
    return amount && amount?.fixed_amount > 0 ? formatCurrency(amount) : t('notAvailable');
  };

  const formattedActivationDate = activationDate ? t('dateGranted', { date: new Date(activationDate) }) : null;

  return (
    <Sheet open={isOpen} onOpenChange={onOpenChange}>
      <SheetContent className={isMobile ? 'max-h-[calc(100vh-64px)]' : ''} side={isMobile ? 'bottom' : 'right'} data-testid="bonus-info-modal">
        <PromotionPodGraphicContainer data={{ image, status }}>
          {gameLogo && <Image src={gameLogo} alt="Game logo" width={100} height={100} className="absolute inset-0 size-full object-contain" />}
          <GraphicContainer className="bg-transparent" maskClassName="fill-container-base" />
        </PromotionPodGraphicContainer>
        <div className={classes.contentContainer}>
          <PromotionPodHeader data={{ expirationDate, title, description, status }} variant={variant} />

          {shouldShowProgressBar && data.progress && <PromotionProgressBar {...data.progress} balanceText={bonusBalanceText} progressText={bonusProgressText} showBalance />}

          <div className="flex flex-col w-full bg-container p-3 paragraph-s-strong rounded-md gap-2">
            <div className="flex justify-between">
              <span>{t('bonusRewarded')}</span>
              <span>{formatAmountOrNA(amount)}</span>
            </div>
            <div className="flex justify-between">
              <span className="paragraph-s-strong">{t('validOn')}</span>
              <span className="paragraph-s-strong">{validOn}</span>
            </div>
            {formattedActivationDate ? (
              <div className="flex justify-between">
                <span className="paragraph-s-strong">{t('bonusGranted')}</span>
                <span className="paragraph-s-strong">{formattedActivationDate}</span>
              </div>
            ) : null}
            {showMaxLimits && (
              <>
                <div className="flex justify-between">
                  <span className="paragraph-s-strong flex gap-1">
                    {t('maximumBonusBet')}
                    <IconWithTooltip tooltipContent={t('bonusMaxBetTooltip')}>
                      <InfoCircle size="extra-small" />
                    </IconWithTooltip>
                  </span>
                  <span className="paragraph-s-strong">{formatAmountOrNA(maxBet)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="paragraph-s-strong flex gap-1">
                    {t('maximumWin')}
                    <IconWithTooltip tooltipContent={t('bonusMaxWinTooltip')}>
                      <InfoCircle size="extra-small" />
                    </IconWithTooltip>
                  </span>
                  <span className="paragraph-s-strong">{formatAmountOrNA(maxWinnings)}</span>
                </div>
              </>
            )}
          </div>
        </div>
        {bundleCode ? (
          <SheetFooter className="pt-4 pb-5 px-5">
            <Button asChild className="w-full" variant="secondary" size="m">
              <Link href={ROUTES.OFFER_BUNDLE(bundleCode, iterationNr)}>{t('bonusViewInOffers')}</Link>
            </Button>
          </SheetFooter>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}

export { BonusInfoModal };
